// Live stream client for GET /api/ws
// (docs/adr/2026-09-28-realtime-stream-and-frontend-stack.md): the snapshot
// seeds the query cache, deltas patch it in place, and the UI shows a banner
// when nothing has arrived for 20 s (PRD §5.11).
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";

import {
  getGetAdhocRequestQueryKey, getGetIncidentQueryKey, getListIncidentsQueryKey, getListMonitorStatesQueryKey,
  getListProbesQueryKey,
  type Incident, type MonitorState, type Probe, type StreamMessage,
} from "@/api/generated";
import { useUI } from "@/store/ui";

/** connecting: first connect; live: messages flowing; offline: socket closed,
 * reconnecting; stale: nothing received for STALE_AFTER_MS (open or not). */
export type StreamState = "connecting" | "live" | "stale" | "offline";

type StreamInfo = { state: StreamState; lastAt: number | null };

const StreamContext = createContext<StreamInfo>({ state: "connecting", lastAt: null });

export const STALE_AFTER_MS = 20_000;

export function useStreamState(): StreamInfo {
  return useContext(StreamContext);
}

function upsert<T>(list: T[] | undefined, item: T, key: (t: T) => string): T[] {
  const out = list ? [...list] : [];
  const i = out.findIndex((x) => key(x) === key(item));
  if (i >= 0) out[i] = item;
  else out.push(item);
  return out;
}

let audio: AudioContext | null = null;

function beep() {
  try {
    audio ??= new AudioContext();
    if (audio.state === "suspended") {
      // An AudioContext created before any user gesture starts suspended and
      // stays that way; resume it so the alert actually sounds.
      void audio.resume();
    }
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.12, audio.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + 0.45);
    osc.connect(gain).connect(audio.destination);
    osc.start();
    osc.stop(audio.currentTime + 0.5);
  } catch {
    // audio not permitted until the user interacts with the page
  }
}

export function applyMessage(qc: QueryClient, msg: StreamMessage) {
  switch (msg.type) {
    case "snapshot":
      qc.setQueryData(getListMonitorStatesQueryKey(), msg.monitors ?? []);
      qc.setQueryData(getListIncidentsQueryKey(), msg.incidents ?? []);
      if (msg.probes) qc.setQueryData(getListProbesQueryKey(), msg.probes);
      break;
    case "monitor_state":
      if (msg.monitor) {
        qc.setQueryData<MonitorState[]>(getListMonitorStatesQueryKey(), (l) => upsert(l, msg.monitor!, (m) => m.monitor_id));
      }
      break;
    case "incident": {
      const inc = msg.incident;
      if (!inc) break;
      const key = getListIncidentsQueryKey();
      const before = qc.getQueryData<Incident[]>(key);
      const isNew = !before?.some((i) => i.id === inc.id);
      qc.setQueryData<Incident[]>(key, (l) =>
        inc.status === "RESOLVED" ? (l ?? []).filter((i) => i.id !== inc.id) : upsert(l, inc, (i) => i.id),
      );
      qc.invalidateQueries({ queryKey: getGetIncidentQueryKey(inc.id) });
      qc.invalidateQueries({ queryKey: [key[0]], predicate: (q) => q.queryKey.length > 1 });
      if (isNew && inc.status === "OPEN" && !inc.suppressed_by && (inc.severity === "DOWN" || inc.severity === "CRITICAL") && useUI.getState().audible) {
        beep();
      }
      break;
    }
    case "probe_state":
      if (msg.probe) {
        qc.setQueryData<Probe[]>(getListProbesQueryKey(), (l) => upsert(l, msg.probe!, (p) => p.id));
        // The include_retired list (its own key) holds every probe, so patch it
        // the same way; refetching it would run on every heartbeat.
        qc.setQueriesData<Probe[]>(
          { queryKey: [getListProbesQueryKey()[0]], predicate: (q) => q.queryKey.length > 1 },
          (l) => l && upsert(l, msg.probe!, (p) => p.id),
        );
      }
      break;
    case "adhoc_result":
      if (msg.adhoc) qc.setQueryData(getGetAdhocRequestQueryKey(msg.adhoc.id), msg.adhoc);
      break;
  }
}

export function StreamProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [info, setInfo] = useState<StreamInfo>({ state: "connecting", lastAt: null });

  useEffect(() => {
    let ws: WebSocket | null = null;
    let stopped = false;
    let retry = 1000;
    // Staleness counts from mount until the first message arrives.
    let last = Date.now();
    let lastMessage: number | null = null;
    let reconnect: number | undefined;
    const set = (state: StreamState) => setInfo((cur) => (cur.state === state && cur.lastAt === lastMessage ? cur : { state, lastAt: lastMessage }));
    const isStale = () => Date.now() - last > STALE_AFTER_MS;

    const connect = () => {
      set(isStale() ? "stale" : lastMessage === null ? "connecting" : "offline");
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      ws = new WebSocket(`${proto}://${window.location.host}/api/ws`);
      ws.onopen = () => {
        retry = 1000;
      };
      ws.onmessage = (ev) => {
        last = lastMessage = Date.now();
        set("live");
        try {
          applyMessage(qc, JSON.parse(ev.data) as StreamMessage);
        } catch (e) {
          console.error("stream message", e);
        }
      };
      ws.onclose = () => {
        if (stopped) return;
        set(isStale() ? "stale" : "offline");
        reconnect = window.setTimeout(connect, retry + Math.random() * 500);
        retry = Math.min(retry * 2, 30_000);
      };
    };
    const stale = window.setInterval(() => {
      if (isStale()) set("stale");
    }, 2000);
    connect();
    return () => {
      stopped = true;
      window.clearInterval(stale);
      window.clearTimeout(reconnect);
      ws?.close();
    };
  }, [qc]);

  return <StreamContext.Provider value={info}>{children}</StreamContext.Provider>;
}
